import { Row, Col } from "reactstrap";
import { useState } from "react";
import { Button, Steps } from "antd";
import "../utils/firstBusiness.css";
import FirstBusinessModal from "./firstBusinessModal";
import * as Yup from "yup";
import { toast } from "react-toastify";
import FormField from "../../../common/Form/FormField";
import SelectField from "../../../common/Form/SelectField";
import { useFormik } from "formik";
import { useQuery } from "react-query";
import { getPaymentMethods } from "../../transactions/services/transactionApi";
import PaymentMethodSelector, { countryInfo } from "../../developer/component/PaymentMethodSelector";
import { useNavigate } from "react-router-dom";
import { addBusiness } from "../../developer/services/developerApi";
import SelectFieldWithFlags from "../../../common/Form/SelectFieldFlags";
import Lottie from "lottie-react";
import loadingAnim from "../../../assets/animations/loading.json";
import { useAuth } from "../../../common/context/AuthContext";

const { Step } = Steps;

const FirstBusiness = () => {
  const navigate = useNavigate();
  const [showTour, setShowTour] = useState(true);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const currentUser = JSON.parse(sessionStorage.getItem("authUser"));
  const merchandId = currentUser?.user?.id || currentUser?.userId;

  const { data = [], isLoading: isLoadingPaym } = useQuery("paymentList", getPaymentMethods);
  const paymentMethodList = data?.data;

  const countries = Object.entries(countryInfo).map(([code, info]) => ({
    value: code,
    label: info.name,
    flag: info.flag,
  }));

  const handleCloseModal = () => {
    setShowTour(false);
  };

  const validationSchema = Yup.object().shape({
    businessName: Yup.string().required("Veuillez renseigner le nom"),
    type: Yup.string().required("Veuillez renseigner le type"),
    sector: Yup.string().required("Veuillez renseigner le secteur"),
    country: Yup.string().required("Veuillez sélectionner un pays"),
    countryPaymentMethods: Yup.array().min(1, "Sélectionnez au moins une méthode de paiement").required(),
  });

  const initialValues = {
    businessName: "",
    type: "",
    sector: "",
    country: "",
    description: "",
    countryPaymentMethods: [],
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setIsLoadingSubmit(true);
      const mappedPaymentMethods = values.countryPaymentMethods.map((countryBlock) => ({
        country: countryBlock.country,
        paymentMethods: countryBlock.paymentMethods.map((method) => ({
          id: method.id,
          name: method.name,
          type: method.type,
          prefixCode: method.prefixCode,
          currency: method.currency,
          urlLogo: method.urlLogo,
        })),
      }));
      try {
        const payload = { ...values, countryPaymentMethods: mappedPaymentMethods, merchandId };
        await addBusiness(payload);
        toast.success("Bravo ! Business créé avec succès 🎉");
        navigate(`/dashboard`);        
        formik.resetForm();
      } catch (error) {
        toast.error(error || "Une erreur est survenue, veuillez réessayer.");
      } finally {
        setIsLoadingSubmit(false);
      }
    },
  });

  const typeList = [
    { id: "PARTICULAR", label: "Particulier" },
    { id: "COMPANY", label: "Entreprise" }
  ];

  const Step1Content = () => (
    <Row>
      <FormField label="Nom de votre entreprise" name="businessName" formik={formik} colSize={6} />
      <SelectField label="Type" name="type" nameSelect="le type du business" formik={formik} options={typeList} error={formik.errors.type} colSize={6} />
      <FormField label="Secteur d'activité" name="sector" formik={formik} colSize={6} />
      <SelectFieldWithFlags label="Pays" name="country" nameSelect="un pays" formik={formik} options={countries} error={formik.errors.country} colSize={6} isName={true} />
      <FormField label="Description" name="description" formik={formik} colSize={12} isTextarea={true} />
    </Row>
  );

  const Step2Content = () => (
    <PaymentMethodSelector
      paymentList={paymentMethodList}
      value={formik.values.countryPaymentMethods}
      onChange={(val) => formik.setFieldValue("countryPaymentMethods", val)}
    />
  );

  const Step3Content = () => (
    <PaymentMethodSelector
      paymentList={paymentMethodList}
      value={formik.values.countryPaymentMethods}
      onChange={(val) => formik.setFieldValue("countryPaymentMethods", val)}
    />
  );

  const steps = [
    {
      title: "Infos basiques",
      content: Step1Content,
    },
    {
      title: "Méthodes de paiement",
      content: Step2Content,
    },
    {
      title: "Document",
      content: Step3Content,
    },
  ];

  const next = async () => {
    if (currentStep === 0) {
      try {
        await validationSchema.validateAt("businessName", formik.values);
        await validationSchema.validateAt("type", formik.values);
        await validationSchema.validateAt("sector", formik.values);
        await validationSchema.validateAt("country", formik.values);
        setCurrentStep(1);
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  const prev = () => setCurrentStep(currentStep - 1);

  return (
    <div className="new-merchant-container" style={{ position: "relative" }}>
      <Row className="merchant-content">
        <Col md="8">
          <h4 className="text-center title-addEdit-Step mb-2">Créer un nouveau business</h4>

          <Row className="container-steps">
            <Col lg={3} className="sidebar-steps-bus">
              <Steps direction="vertical" current={currentStep}>
                {steps.map((step, index) => (
                  <Step key={index} title={step.title} />
                ))}
              </Steps>
            </Col>

            <Col lg={9} className="content-steps">
              {steps[currentStep].content()}
              <div className="stepper-actions mt-4 d-flex justify-content-between">
                {currentStep > 0 && (
                  <Button onClick={prev}>Précédent</Button>
                )}
                {currentStep < steps.length - 1 && (
                  <Button type="primary" onClick={next}>
                    Suivant
                  </Button>
                )}
                {currentStep === steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={formik.handleSubmit}
                    disabled={isLoadingSubmit}
                  >
                    Valider
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </Col>
      </Row>

      {showTour && <FirstBusinessModal onClose={handleCloseModal} />}

      {isLoadingSubmit && (
        <div className="fc-loading-overlay">
          <Lottie animationData={loadingAnim} loop={true} style={{ width: 150, height: 150 }} />
        </div>
      )}
    </div>
  );
};

export default FirstBusiness;
